<template>
  <div class="combined-otp-chart">
    <highcharts ref="chart" :options="chartOptions" />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsExporting from 'highcharts/modules/exporting';
import { Chart } from 'highcharts-vue';
import { exportChartAsPNG, exportAsCSV, prepareOTPDataForExport } from '../utils/chartExport';
import { exportOTPAsExcel } from '../utils/otpReportExport';
import { positionOtpTooltip, formatOtpDateTooltip, formatOtpBucketLabel } from '../utils/otpFormatters';

HighchartsMore(Highcharts);
HighchartsExporting(Highcharts);

export default {
  name: 'CombinedOtpChart',
  components: {
    highcharts: Chart,
  },
  props: {
    lateArrivals: {
      type: Array,
      required: true,
    },
    earlyDepartures: {
      type: Array,
      required: true,
    },
    otpPercentages: {
      type: Array,
      required: true,
    },
    leftBehindData: {
      type: Array,
      default: () => [],
    },
    dateStats: {
      type: Array,
      default: () => [],
    },
    threshold: {
      type: Number,
      default: 5,
    },
    dateRange: {
      type: Object,
      required: false,
      default: null,
    },
  },
  computed: {
    leftBehindCount() {
      return this.leftBehindData.reduce((sum, item) => sum + (item.count || 0), 0);
    },
    ...mapState('otpDashboard', ['filters']),
    chartOptions() {
      // One category per bucket (hour, day, week or month, by the selected range's length).
      // Categories are the bucket keys, which the tooltip matches on; the axis shows labels.
      const categories = this.lateArrivals.map((item) => item.key);
      const { locale } = this.$i18n;
      const labelsByKey = new Map(this.lateArrivals.map((item) => [item.key, formatOtpBucketLabel(item, locale)]));
      const period = this.lateArrivals[0]?.period;

      // Box plot data for late arrivals (positive values).
      // Empty days (no trips) emit null so Highcharts renders a gap instead of
      // a zero box, while the category is still present on the x-axis.
      const lateBoxPlotData = this.lateArrivals.map((item) => {
        if (item.min === null || item.median === null) return null;
        return [item.min, item.q1, item.median, item.q3, item.max];
      });

      // Box plot data for early departures (negative values)
      const earlyBoxPlotData = this.earlyDepartures.map((item) => {
        if (item.min === null || item.median === null) return null;
        return [-item.max, -item.q3, -item.median, -item.q1, -item.min];
      });

      // OTP percentages. null for empty days breaks the line (connectNulls defaults to false).
      const percentages = this.otpPercentages.map((item) => item.percentage);

      // Transparent anchor points at empty-day categories (y=0 on the deviation axis).
      // Without a mouse-trackable point at empty x-positions the shared tooltip never
      // fires, so the "No trips on this date" branch in the formatter would be
      // unreachable. These invisible scatter points give Highcharts something to track.
      const emptyDayAnchors = this.lateArrivals.map((item) => (item.min === null || item.median === null ? 0 : null));

      // Calculate Y-axis range based on whisker values only (excludes outliers)
      // Using only min/max from boxplot which represent the whiskers, not outlier points
      const whiskerValues = [
        ...this.lateArrivals.map((item) => item.max).filter((val) => val !== null && val !== undefined),
        ...this.earlyDepartures.map((item) => -item.max).filter((val) => val !== null && val !== undefined),
        ...this.lateArrivals.map((item) => item.min).filter((val) => val !== null && val !== undefined),
        ...this.earlyDepartures.map((item) => -item.min).filter((val) => val !== null && val !== undefined),
      ];

      const dataMin = whiskerValues.length > 0 ? Math.min(...whiskerValues) : -10;
      const dataMax = whiskerValues.length > 0 ? Math.max(...whiskerValues) : 10;

      // Add 10% padding to the range for better visualization
      const range = dataMax - dataMin;
      const padding = range * 0.1;
      const yAxisMin = Math.floor(dataMin - padding);
      const yAxisMax = Math.ceil(dataMax + padding);

      // Store references for tooltip formatter closure
      const lateArrivalsData = this.lateArrivals;
      const earlyDeparturesData = this.earlyDepartures;
      const otpPercentagesData = this.otpPercentages;
      const dateStatsData = this.dateStats;
      const t = this.$t.bind(this);

      return {
        chart: {
          type: 'boxplot',
          height: 500,
          zoomType: 'xy',
        },
        credits: {
          enabled: false,
        },
        exporting: {
          enabled: false,
        },
        title: {
          text: null,
        },
        xAxis: {
          categories,

          labels: {
            formatter() {
              const index = this.pos;
              const totalPoints = categories.length;
              const lastIndex = totalPoints - 1;

              const formatLabel = (value) => labelsByKey.get(value) || '';

              // Always show first and last labels
              if (index === 0 || index === lastIndex) {
                return formatLabel(this.value);
              }

              // Estimate per-label width ("9/24" ~ 40px; "6:00 AM", "Sep 22-28" and
              // "Sep 2026" ~ 70px) and compute a step so labels don't overlap based on the
              // actual plot width. Falls back to showing all labels when plot width isn't
              // available yet.
              const labelWidth = period === 'daily' ? 40 : 70;
              const plotWidth = this.chart && this.chart.plotWidth;
              if (!plotWidth) {
                return formatLabel(this.value);
              }

              const step = Math.max(1, Math.ceil((totalPoints * labelWidth) / plotWidth));
              if (index % step === 0) {
                return formatLabel(this.value);
              }
              return '';
            },
            rotation: 0,
            align: 'center',
            y: 40,
            useHTML: true,
            style: {
              textAlign: 'center',
            },
          },
          tickmarkPlacement: 'on',
          gridLineWidth: 1,
          gridLineColor: '#e6e6e6',
          tickInterval: 1,
          minorGridLineWidth: 0,
          minorTickInterval: null,
        },
        yAxis: [
          {
            // Left Y-axis for deviations
            title: {
              text: this.$t('flexigo.otp.otpDeviationMin'),
              style: {
                color: '#666',
              },
            },
            labels: {
              format: '{value}',
              style: {
                color: '#666',
              },
            },
            min: yAxisMin,
            max: yAxisMax,
            plotLines: [
              {
                value: this.threshold,
                color: '#ff8b39',
                dashStyle: 'dash',
                width: 1,
                zIndex: 5,
              },
              {
                value: 0,
                color: '#999',
                width: 1,
                zIndex: 5,
              },
            ],
          },
          {
            // Right Y-axis for OTP percentage
            title: {
              text: this.$t('flexigo.otp.onTimePerformancePercent'),
              style: {
                color: '#1cab49',
              },
            },
            labels: {
              format: '{value}%',
              style: {
                color: '#1cab49',
              },
            },
            min: 0,
            max: 100,
            opposite: true,
          },
        ],
        legend: {
          enabled: true,
          align: 'center',
          verticalAlign: 'bottom',
          margin: 20,
          padding: 20,
          itemStyle: {
            color: '#687692',
            fontSize: '.9em',
            fontWeight: 'normal',
          },
        },
        tooltip: {
          shared: true,
          useHTML: true,
          backgroundColor: '#363647',
          borderColor: '#363647',
          borderRadius: 6,
          style: {
            color: '#fff',
          },
          positioner(labelWidth, labelHeight, point) {
            return positionOtpTooltip(labelWidth, labelHeight, point, this.chart);
          },
          formatter() {
            return formatOtpDateTooltip(
              this.x,
              lateArrivalsData,
              earlyDeparturesData,
              otpPercentagesData,
              dateStatsData,
              t,
            );
          },
        },
        plotOptions: {
          boxplot: {
            pointWidth: 10,
            grouping: false,
            pointPlacement: 0,
          },
        },
        series: [
          {
            name: this.$t('flexigo.otp.lateDeviationMin'),
            data: lateBoxPlotData,
            color: '#ff8b39',
            fillColor: '#ff8b39',
            yAxis: 0,
          },
          {
            name: this.$t('flexigo.otp.earlyDeviationMin'),
            data: earlyBoxPlotData,
            color: '#5287e3',
            fillColor: '#5287e3',
            yAxis: 0,
          },
          {
            name: this.$t('flexigo.otp.onTimePerformancePercent'),
            type: 'line',
            data: percentages,
            color: '#1cab49',
            yAxis: 1,
            marker: {
              enabled: true,
              radius: 7,
              symbol: 'circle',
              fillColor: 'white',
              lineWidth: 3,
              lineColor: '#1cab49',
            },
            dataLabels: {
              enabled: false,
              format: '{y}%',
              style: {
                color: '#1cab49',
                fontWeight: 'bold',
              },
            },
            tooltip: {
              headerFormat: '<em>Date: {point.key}</em><br/>',
              pointFormat: '<span style="color:{point.color}">●</span> OTP: <b>{point.y}%</b><br/>',
            },
          },
          {
            name: `Left Behind (${this.leftBehindCount})`,
            type: 'scatter',
            data: [],
            color: '#af52de',
            marker: {
              enabled: true,
              radius: 5,
              symbol: 'circle',
            },
            showInLegend: true,
            enableMouseTracking: false,
          },
          {
            // Transparent anchor series: invisible scatter points at empty-day
            // categories so the shared tooltip fires on hover. Without this, days
            // where all other series are null have no trackable point and the
            // tooltip (including the "No trips on this date" message) never shows.
            name: 'Empty Day Anchor',
            type: 'scatter',
            data: emptyDayAnchors,
            yAxis: 0,
            marker: {
              enabled: true,
              radius: 18,
              symbol: 'rect',
              fillColor: 'rgba(0,0,0,0)',
              lineWidth: 0,
              states: {
                hover: {
                  enabled: false,
                },
              },
            },
            showInLegend: false,
            enableMouseTracking: true,
            tooltip: {
              pointFormat: '',
            },
          },
          {
            name: `${this.$t('flexigo.otp.lateArrivalThresholdLabel')} (+${this.threshold} ${this.$t(
              'flexigo.otp.minutes',
            )})`,
            type: 'line',
            data: [],
            color: '#ff8b39',
            dashStyle: 'dash',
            lineWidth: 1,
            marker: {
              enabled: false,
            },
            showInLegend: true,
            enableMouseTracking: false,
          },
        ],
      };
    },
  },
  methods: {
    exportAsPNG() {
      // Access the Highcharts chart instance from highcharts-vue wrapper
      const chartInstance = this.$refs.chart && this.$refs.chart.chart;
      if (!chartInstance) {
        console.error('Chart instance not available');
        return;
      }
      // Export with fixed dimensions: 1200x600 at 2x scale for high quality
      exportChartAsPNG(chartInstance, 'otp-by-date', {
        width: 1200,
        height: 600,
        scale: 2,
        surface: 'combined-otp',
      });
    },
    exportAsCSV() {
      const { headers, rows } = prepareOTPDataForExport(this.lateArrivals, this.earlyDepartures, this.otpPercentages);
      exportAsCSV(headers, rows, 'otp-by-date', 'combined-otp');
    },
    async exportAsExcel() {
      // Use the reusable helper function for OTP Excel export with filters from store
      await exportOTPAsExcel(this.filters, this.lateArrivals);
    },
  },
};
</script>

<style scoped>
.combined-otp-chart {
  width: 100%;
  min-height: 500px;
}
.highcharts-legend {
  margin-top: 20px;
}
</style>
